import { useMemo } from 'react'
import { useSearchQuotesQuery } from '@paintscout/api'
import type { CalendarEvent } from 'paintscout'

export function useEventsWithInvoiceId(events: CalendarEvent[]): CalendarEvent[] {
  const sortedDealIds = useMemo(() => {
    const ids = new Set<string>()
    for (const event of events) {
      if (event.deal?.id) ids.add(event.deal.id)
    }
    return Array.from(ids).sort()
  }, [events])

  const query = useMemo(() => {
    if (sortedDealIds.length === 0) return ''
    const escaped = sortedDealIds.map((id) => `"${id}"`).join(' OR ')
    return `deal_id:(${escaped}) AND is_invoice:true`
  }, [sortedDealIds])

  const { data } = useSearchQuotesQuery({
    variables: { query, limit: 500, sort: ['-quote_date'], skipSanitize: true },
    skip: !query,
    fetchPolicy: 'cache-and-network'
  })

  const dealIdToInvoiceId = useMemo(() => {
    const map: Record<string, string> = {}
    const invoices = data?.searchQuotes?.quotes ?? []
    for (const q of invoices) {
      if (!q?.deal_id || !q._id) continue
      if (!map[q.deal_id]) map[q.deal_id] = q._id
    }
    return map
  }, [data])

  return useMemo(() => {
    if (Object.keys(dealIdToInvoiceId).length === 0) return events
    return events.map((event) => {
      const invoiceId = event.deal?.id ? dealIdToInvoiceId[event.deal.id] : undefined
      return invoiceId ? { ...event, invoiceId } : event
    })
  }, [events, dealIdToInvoiceId])
}
